/** The sortable columns of each table, and the direction each opens on. */

import type { SortDirection } from "~/pages/manifold-nextgen/components/evals/TableSort";

/**
 * Sort fields for the cases table, across both of its column sets. One list, so
 * the sort survives the switch; a key whose column is off screen stays valid.
 */
export const CaseSortKey = {
  NAME: "name",
  TEXT_SCORE: "text_score",
  FIGURE_SCORE: "figure_score",
  PASS_RATE: "pass_rate",
  FLAKINESS: "flakiness",
  SUITE: "suite",
  DATASETS: "datasets",
  THRESHOLD: "threshold",
  LATENCY: "latency",
  UPDATED: "updated",
} as const;

export type CaseSortKey = (typeof CaseSortKey)[keyof typeof CaseSortKey];

/**
 * The direction each column opens on: whichever end a reviewer came to read.
 * Worst score first, flakiest first, most recently edited first.
 */
export const CASE_SORT_DIRECTIONS: Partial<Record<CaseSortKey, SortDirection>> = {
  [CaseSortKey.TEXT_SCORE]: "asc",
  [CaseSortKey.FIGURE_SCORE]: "asc",
  [CaseSortKey.PASS_RATE]: "asc",
  [CaseSortKey.FLAKINESS]: "desc",
  [CaseSortKey.UPDATED]: "desc",
};

/** Sort fields for the runs table, as clickable column headers. */
export const RunSortKey = {
  STARTED: "started",
  MEAN_SCORE: "mean_score",
  CASES: "cases",
  DURATION: "duration",
  STATUS: "status",
} as const;

export type RunSortKey = (typeof RunSortKey)[keyof typeof RunSortKey];

/**
 * The direction each column opens on: whichever end a reviewer came to read.
 * Newest runs, worst scores, slowest durations.
 */
export const RUN_SORT_DIRECTIONS: Partial<Record<RunSortKey, SortDirection>> = {
  [RunSortKey.STARTED]: "desc",
  [RunSortKey.MEAN_SCORE]: "asc",
  [RunSortKey.CASES]: "desc",
  [RunSortKey.DURATION]: "desc",
  [RunSortKey.STATUS]: "asc",
};

/** Sort fields for a run's per-case results table. */
export const ResultSortKey = {
  CASE: "case",
  SCORE: "score",
  OUTCOME: "outcome",
  DURATION: "duration",
} as const;

export type ResultSortKey = (typeof ResultSortKey)[keyof typeof ResultSortKey];

/** Worst outcomes and lowest scores are what a reviewer opens a run for. */
export const RESULT_SORT_DIRECTIONS: Partial<Record<ResultSortKey, SortDirection>> = {
  [ResultSortKey.CASE]: "asc",
  [ResultSortKey.DURATION]: "desc",
  [ResultSortKey.OUTCOME]: "asc",
  [ResultSortKey.SCORE]: "asc",
};
